/**
 * GILNudge.jsx - Proactive GIL Nudge Component
 * 
 * GOVERNANCE BOUNDARY:
 * - AI explains; AI never decides
 * - Non-blocking, dismissable nudge
 * - Respects session dismissal
 * 
 * Feature-flagged under GIL_PROACTIVE_ENABLED
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Sparkles, X, MessageSquare } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { FrictionDetector, FRICTION_SIGNALS } from '../../lib/gil/FrictionDetector';
import { getGilPrompt, getPageTypeFromRoute } from '../../lib/gil/gilPrompts';
import { isGilProactiveEnabled } from '../../lib/config/featureFlags';
import { cn } from '../../lib/utils';

const GILNudge = ({ onExplainWithGil }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [isDismissedThisSession, setIsDismissedThisSession] = useState(false);
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();

  // Handle friction signal
  const handleFrictionSignal = useCallback((signal) => {
    if (isDismissedThisSession) return;
    
    const pageType = getPageTypeFromRoute(location.pathname);
    const prompt = getGilPrompt(pageType, signal);
    
    setCurrentPrompt(prompt);
    setIsVisible(true);
  }, [location.pathname, isDismissedThisSession]);

  // Initialize friction detector
  useEffect(() => {
    if (!isGilProactiveEnabled()) return;
    
    FrictionDetector.start();
    const unsubscribe = FrictionDetector.subscribe(handleFrictionSignal);
    
    return () => {
      unsubscribe();
      FrictionDetector.stop();
    };
  }, [handleFrictionSignal]);

  // Track navigation for friction detection
  useEffect(() => {
    if (!isGilProactiveEnabled()) return;
    FrictionDetector.recordNavigation(location.pathname);
  }, [location.pathname]);

  // Dismiss nudge
  const handleDismiss = () => {
    setIsVisible(false);
  };

  // Dismiss for session
  const handleDismissSession = () => {
    setIsVisible(false);
    setIsDismissedThisSession(true);
  };

  // Handle explain action
  const handleExplain = () => {
    setIsVisible(false);
    if (onExplainWithGil) {
      onExplainWithGil(currentPrompt);
    }
  };

  // Don't render if feature is disabled
  if (!isGilProactiveEnabled()) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ 
            duration: shouldReduceMotion ? 0 : 0.2,
            ease: [0.2, 0, 0, 1]
          }}
          className={cn(
            "fixed bottom-20 right-6 z-50",
            "max-w-[320px] p-4 rounded-xl",
            "bg-background border border-border shadow-lg",
            "dark:bg-[var(--g-surface-2)] dark:border-[var(--g-border)]"
          )}
          data-testid="gil-nudge"
        >
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-md bg-primary/10">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              GIL Suggestion
            </span>
          </div>

          {/* Prompt */}
          <p className="text-sm text-foreground mb-4 pr-4">
            {currentPrompt}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleExplain}
              className="flex-1"
            >
              <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
              Explain with GIL
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismissSession}
              className="text-muted-foreground"
            >
              Not now
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GILNudge;
